/**
 * pages/MyAlerts.jsx
 * ------------------
 * Full property-alerts (saved searches) view. Allows deleting alerts.
 *
 * v2.71.28 — Initial.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { Bell, ArrowRight, ChevronLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";
import { DashboardLayout, RecentItemsList } from "../features/dashboard";
// v2.80 — Cross-session refresh when saved searches mutate elsewhere
// (e.g. user deletes an alert in another tab).
import { useDataSync } from "../lib/dataSync";

export default function MyAlerts() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/saved-searches")
      .then((r) => setItems(r.data?.results || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    return undefined;
  }, [user, load]);

  // v2.80 — Sync across tabs: deleting an alert in tab-A updates tab-B.
  useDataSync(["alerts", "saved_searches"], () => { if (user) load(); });

  const deleteAlert = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    try {
      await api.delete(`/saved-searches/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Alert deleted");
    } catch {
      toast.error("Could not delete alert");
    }
  };

  if (authLoading) return <DashboardLayout title="Property Alerts" sidebar />;
  if (!user) return <Navigate to="/login?next=/account/alerts" replace />;

  return (
    <DashboardLayout
      title="Property Alerts"
      subtitle={loading ? "Loading…" : `${items.length} saved`}
      actions={
        <Link
          to="/account"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#71717A] hover:text-[#09090B]"
          data-testid="back-to-account"
        >
          <ChevronLeft size={14} /> Account
        </Link>
      }
    >
      <RecentItemsList
        title={null}
        items={loading ? [] : items}
        emptyHint={loading ? "Loading…" : "Save a search from the Browse page to start receiving alerts."}
        testid="alerts-full-list"
        renderItem={(a) => {
          const f = a.filters || {};
          const bits = [f.main_area, f.bhk, f.property_type, f.listing_intent].filter(Boolean);
          const qs = new URLSearchParams(
            Object.fromEntries(
              Object.entries(f).filter(
                ([k, v]) => v && k !== "priceRange" && k !== "maxMetro"
              )
            )
          ).toString();
          return (
            <div className="flex items-start gap-3" data-testid={`full-alert-${a.id}`}>
              <div className="w-12 h-12 bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#71717A]">
                <Bell size={18} />
              </div>
              <Link to={`/?${qs}`} className="flex-1 min-w-0 group">
                <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                  {a.name || "Untitled alert"}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                  {bits.length ? bits.join(" · ") : "Custom filter"}
                </div>
              </Link>
              <button
                type="button"
                onClick={() => deleteAlert(a.id)}
                aria-label="Delete alert"
                className="w-8 h-8 no-touch-min flex items-center justify-center text-[#A1A1AA] hover:text-[#DC2626] hover:bg-[#FEE2E2] rounded-sm transition-colors flex-shrink-0"
                data-testid={`delete-alert-${a.id}`}
              >
                <Trash2 size={14} />
              </button>
              <Link to={`/?${qs}`} aria-label="Open alert">
                <ArrowRight size={14} className="text-[#A1A1AA] hover:text-[#0055FF] mt-2" />
              </Link>
            </div>
          );
        }}
      />
    </DashboardLayout>
  );
}
